import React, { useEffect, useState } from 'react';
import SearchBar from './SearchBar';
import PaginationControls from './PaginationControls';
import responsibleService from '../api.js';
import { ArrowUp, ArrowDown, X } from 'lucide-react';

const PromptsTable = ({
  promptData,
  isLoading,
  pageSize,
  setPageSize,
  currentPage,
  setCurrentPage,
  totalPages,
  totalRecords,
  searchTerm,
  setSearchTerm,
  searchField,
  setSearchField,
  isSearching,
  handleSearch,
  clearSearch,
  handlePromptSelect,
  setSelectedGuardrail
}) => {

  const [sortField, setSortField] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortedData, setSortedData] = useState([]);

  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const [selectedSources, setSelectedSources] = useState(null);
  const [expandedSource, setExpandedSource] = useState(null);

  const [selectedRow, setSelectedRow] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryContent, setSummaryContent] = useState('');
  const [isSummarizing, setIsSummarizing] = useState(false);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 6
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatTime = (value) => {
    if (value === undefined || value === null) {
      return "0.00s";
    }
    return `${value.toFixed(2)}s`;
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const renderSortIndicator = (field) => {
    if (sortField !== field) return null;

    return sortDirection === 'asc'
      ? <ArrowUp className="inline ml-1" size={14} />
      : <ArrowDown className="inline ml-1" size={14} />;
  };

  const handleShowSources = (sources) => {
    if (typeof sources === 'string') {
      try {
        const parsedSources = JSON.parse(sources);
        setSelectedSources(parsedSources);
      } catch (e) {
        setSelectedSources(sources);
      }
    } else {
      setSelectedSources(sources);
    }
    setShowSourcesModal(true);
  };

  const handleRowSelect = (prompt) => {
    setSelectedRow(selectedRow?.id === prompt.id ? null : prompt);
  };

  const handleSummarize = async () => {
    if (!selectedRow || !selectedRow.run_id) {
      alert('No valid run ID found for the selected row');
      return;
    }

    setIsSummarizing(true);
    try {
      const response = await responsibleService.getSummarizedAudit(selectedRow.run_id);
      setSummaryContent(response.response || 'No summary available.');
      setShowSummaryModal(true);
    } catch (error) {
      console.error('Error getting summary:', error);
      alert('Failed to get summary. Please try again.');
    } finally {
      setIsSummarizing(false);
    }
  };

  useEffect(() => {
    if (!promptData || promptData.length === 0) {
      setSortedData([]);
      return;
    }

    const sorted = [...promptData].sort((a, b) => {
      // Handle different data types appropriately
      if (sortField === 'timestamp') {
        return sortDirection === 'asc'
          ? new Date(a.timestamp) - new Date(b.timestamp)
          : new Date(b.timestamp) - new Date(a.timestamp);
      }

      // Numeric fields
      if (['cost', 'totalTokens', 'inputTokens', 'outputTokens', 'latency'].includes(sortField)) {
        // Handle null/undefined values
        const valA = a[sortField] === null || a[sortField] === undefined ? 0 : a[sortField];
        const valB = b[sortField] === null || b[sortField] === undefined ? 0 : b[sortField];
        return sortDirection === 'asc' ? valA - valB : valB - valA;
      }

      // String fields (case-insensitive)
      const valA = (a[sortField] || '').toString().toLowerCase();
      const valB = (b[sortField] || '').toString().toLowerCase();

      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setSortedData(sorted);
  }, [promptData, sortField, sortDirection]);

  const SourcesModal = () => {
    if (!showSourcesModal || !selectedSources) return null;

    const isFormattedSource = Array.isArray(selectedSources) &&
      selectedSources.length > 0 &&
      typeof selectedSources[0] === 'object' &&
      'content' in selectedSources[0] &&
      'score' in selectedSources[0];

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm bg-black/50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-semibold">
              {isFormattedSource
                ? `Document Sources (${selectedSources.length})`
                : "Source Information"}
            </h2>
            <button
              onClick={() => setShowSourcesModal(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="overflow-y-auto p-4 flex-grow">
            {isFormattedSource ? (
              selectedSources.map((source, index) => (
                <div key={index} className="mb-4 border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium">{source.title || 'Untitled Source'}</div>
                      <div className="text-sm text-gray-500">
                        {source.source} {source.page_label ? `- Page ${source.page_label}` : ''}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Relevance Score: {(source.score * 100).toFixed(1)}%
                      </div>
                    </div>
                    <button
                      onClick={() => setExpandedSource(expandedSource === index ? null : index)}
                      className="text-primary hover:text-primaryDark text-sm px-3 py-1 border border-primary rounded-md"
                    >
                      {expandedSource === index ? 'Hide Content' : 'Show Content'}
                    </button>
                  </div>

                  {expandedSource === index && (
                    <div className="mt-3 p-3 bg-white border border-gray-200 rounded text-sm text-gray-700 max-h-60 overflow-y-auto whitespace-pre-wrap">
                      {source.content}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="p-3 bg-white border border-gray-200 rounded text-sm text-gray-700 whitespace-pre-wrap">
                {typeof selectedSources === 'string'
                  ? selectedSources
                  : JSON.stringify(selectedSources, null, 2)}
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={() => setShowSourcesModal(false)}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const SummaryModal = () => {
    if (!showSummaryModal) return null;

    const renderFormattedSummary = (text) => {
      if (!text) return <p>No summary available</p>;

      const sections = text.split('\n\n');

      return (
        <div className="text-gray-700">
          {sections.map((section, sectionIndex) => {
            if (!section.trim().startsWith('-') && !section.trim().startsWith('*')) {
              return <p key={sectionIndex} className="font-medium mb-4">{section}</p>;
            }

            const lines = section.split('\n');
            const sectionTitle = lines[0].replace(/^- \*\*([^:]+):\*\*$/, '$1');
            const isSectionHeader = sectionTitle !== lines[0];

            return (
              <div key={sectionIndex} className="mb-4">
                {isSectionHeader ? (
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{sectionTitle}</h3>
                ) : null}

                <ul className="list-disc pl-5 space-y-1">
                  {lines.map((line, lineIndex) => {
                    if (isSectionHeader && lineIndex === 0) return null;

                    const formattedLine = line.replace(/\*\*([^*]+)\*\*/g, (match, p1) => {
                      return `<strong>${p1}</strong>`;
                    });

                    return (
                      <li
                        key={lineIndex}
                        className="text-sm"
                        dangerouslySetInnerHTML={{
                          __html: formattedLine.replace(/^- /, '')
                        }}
                      />
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm bg-black/30 animate-fadeIn">
        <div className="bg-white rounded-lg shadow-xl w-[90%] max-h-[90%] overflow-hidden flex flex-col" style={{ maxWidth: '700px' }}>
          <div className="p-3 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-base font-semibold">Audit Summary</h2>
            <button
              onClick={() => setShowSummaryModal(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={18} />
            </button>
          </div>

          <div className="overflow-y-auto p-4 flex-grow bg-gray-50">
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
              {renderFormattedSummary(summaryContent)}
            </div>
          </div>

          <div className="p-3 border-t border-gray-200 flex justify-end">
            <button
              onClick={() => setShowSummaryModal(false)}
              className="px-3 py-1 text-sm bg-primary text-white rounded hover:bg-primaryDark transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 py-4 mb-5">
      <SourcesModal />

      <SummaryModal />

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-[14px] font-semibold text-gray-600 uppercase tracking-[0.7px]">Recent Prompts</h2>

        <div className="flex items-center space-x-4">
          {selectedRow && (
            <button
              onClick={handleSummarize}
              disabled={isSummarizing}
              className={`px-3 py-1 rounded text-sm font-medium ${isSummarizing
                ? "bg-gray-300 text-gray-500"
                : "bg-primary text-white hover:bg-primaryDark"
                } transition-colors flex items-center`}
            >
              {isSummarizing ? (
                <>
                  <div className="w-3 h-3 border-2 border-t-white border-white/30 rounded-full animate-spin mr-2"></div>
                  Summarizing...
                </>
              ) : (
                <>Summarize</>
              )}
            </button>
          )}

          <div className="flex items-center">
            <label htmlFor="pageSize" className="mr-2 text-sm text-gray-600">
              Rows per page:
            </label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
              disabled={isLoading}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      <SearchBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searchField={searchField}
        setSearchField={setSearchField}
        handleSearch={handleSearch}
        clearSearch={clearSearch}
        isLoading={isLoading}
        isSearching={isSearching}
      />

      {searchTerm && (
        <div className="mb-4 text-sm text-gray-500">
          {isLoading ? (
            'Searching...'
          ) : (
            <>
              Found <span className="font-medium">{totalRecords}</span> results for "{searchTerm}" in {searchField === 'all' ? 'all fields' : searchField}
            </>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center py-10">
          <div className="w-3 h-3 border-2 border-t-primary border-primaryLight rounded-full animate-spin mr-2"></div>
          <span className="text-sm text-gray-500">Loading...</span>
        </div>
      ) : (
        <>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto scrollbar-none">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider border-b border-gray-200">
                    Select
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200"
                    onClick={() => handleSort('id')}
                  >
                    <div className="flex items-center">
                      ID {renderSortIndicator('id')}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200"
                    onClick={() => handleSort('timestamp')}
                  >
                    <div className="flex items-center">
                      Timestamp {renderSortIndicator('timestamp')}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200"
                    onClick={() => handleSort('domain')}
                  >
                    <div className="flex items-center">
                      Domain {renderSortIndicator('domain')}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200"
                    onClick={() => handleSort('useCase')}
                  >
                    <div className="flex items-center">
                      Use Cases {renderSortIndicator('useCase')}
                    </div>
                  </th>
                   <th
                    className="px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200"
                    onClick={() => handleSort('agent')}
                  >
                    <div className="flex items-center">
                      Agent {renderSortIndicator('agent')}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200"
                    onClick={() => handleSort('customerId')}
                  >
                    <div className="flex items-center">
                      Customer {renderSortIndicator('customerId')}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200"
                    onClick={() => handleSort('endpoint')}
                  >
                    <div className="flex items-center">
                      Endpoint {renderSortIndicator('endpoint')}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200"
                    onClick={() => handleSort('model')}
                  >
                    <div className="flex items-center">
                      Model {renderSortIndicator('model')}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200"
                    onClick={() => handleSort('totalTokens')}
                  >
                    <div className="flex items-center">
                      Tokens {renderSortIndicator('totalTokens')}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200"
                    onClick={() => handleSort('cost')}
                  >
                    <div className="flex items-center">
                      Cost {renderSortIndicator('cost')}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200"
                    onClick={() => handleSort('latency')}
                  >
                    <div className="flex items-center">
                      Latency {renderSortIndicator('latency')}
                    </div>
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider border-b border-gray-200">
                    Reasoning
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider border-b border-gray-200">
                    Sources
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider border-b border-gray-200">
                    Guardrail
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider border-b border-gray-200">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {sortedData.length > 0 ? (
                  sortedData.map((prompt, index) => (
                    <tr
                      key={index}
                      className={`hover:bg-gray-50 transition-colors ${selectedRow?.id === prompt.id ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                        <input
                          type="radio"
                          name="selectedRow"
                          checked={selectedRow?.id === prompt.id}
                          onChange={() => handleRowSelect(prompt)}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {prompt.id}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {new Date(prompt.timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {prompt.domainName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {prompt.useCaseName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {prompt.agentName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {prompt.customerId}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {prompt.endpoint}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {prompt.model}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {formatNumber(prompt.totalTokens)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {formatCurrency(prompt.cost)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {formatTime(prompt.latency)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {prompt.reasoning}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                        {prompt.sources && (typeof prompt.sources === 'string' ? prompt.sources.trim() !== '' : true) ? (
                          <button
                            onClick={() => handleShowSources(prompt.sources)}
                            className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-primaryDark hover:bg-blue-200"
                          >
                            {Array.isArray(prompt.sources) ? (
                              <>
                                <span className="mr-1">{prompt.sources.length}</span>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </>
                            ) : (
                              <>
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View
                              </>
                            )}
                          </button>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                        <button onClick={() => setSelectedGuardrail(prompt.useCaseName ?? "Dashboard")}>
                          {prompt.guardrailBlock === true ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200 shadow-sm">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500 mr-1"></span>
                              Blocked
                            </span>
                          ) : prompt.guardrailBlock === false ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-successLight text-success border border-successLight shadow-sm">
                              <span className="w-1.5 h-1.5 rounded-full bg-success mr-1"></span>
                              Passed
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200 shadow-sm">
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-1"></span>
                              N/A
                            </span>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-center">
                        <button
                          className="text-primary hover:text-primaryDark"
                          onClick={() => handlePromptSelect(prompt)}
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="15" className="px-4 py-3 text-center text-sm text-gray-500">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <PaginationControls
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            totalRecords={totalRecords}
            pageSize={pageSize}
            isLoading={isLoading}
          />
          </div>
        </>
      )}
    </div>
  );
};

export default PromptsTable;